import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * `<sony-iris-lever>` — Sony RCP-3500 IRIS control lever, rendered
 * TOP-DOWN to match the panel's face-elevation view.
 *
 * # Top-down doctrine
 *
 * The entire panel replica renders as a face elevation — every other
 * rotary (`<rotary-encoder>`, `<stacked-rotary>`, the SENS / COARSE /
 * DETAIL knobs, the paint rotaries) is drawn as concentric circles
 * seen from above. The Sony iris lever is the same visual class: from
 * the operator's seated perspective looking straight down at the
 * panel face, the lever assembly reads as a stack of concentric
 * circles. Drawing it as a tall side-on column would put the operator
 * on their knees beside the panel — inconsistent with every other
 * control on the surface.
 *
 * # Geometry
 *
 * The control is a stack of concentric circles (top-down): a chassis
 * well, an outer bezel collar, a knurled master-black rotation ring, a
 * flat cap disc, and a centre pivot. The render below uses a normalised
 * 0..100 viewBox; absolute placement on a panel comes from that panel's
 * own geometry.
 *
 * NOTE: the RCP-3500 panel does NOT mount this primitive — it renders
 * the iris straight from Sony's operating-instructions PDF vector
 * (page-09), which already draws the assembly to scale. This element
 * remains as a standalone, interactive top-down lever for Storybook and
 * any panel that needs a synthetic control rather than the vendor
 * drawing. (Earlier revisions carried hand-traced coordinates from a
 * since-discarded layout SVG; those have been removed.)
 *
 * # Render layers (innermost → outermost = drawn last → first)
 *
 *   1. Chassis well — darkest fill, sets the recess
 *   2. Outer collar — the visible bezel ring around the lever
 *   3. Master-black knurled rotation ring — picks up the
 *      cross-hatch knurl pattern when the operator rolls it
 *   4. Cap top — the matte rubber disc the thumb rests on
 *   5. Inner hub — small contact mark / pivot indicator at centre
 *
 * Each ring carries a subtle gradient so the radial layering reads
 * as a stack of components at different depths rather than as flat
 * concentric paint.
 *
 * # Interaction
 *
 *   - Vertical drag → iris throw (0..1). No visual tilt in top-down
 *     view — the lever stays planar; only the centre hub tick may
 *     rotate slightly to suggest the lever's leaning state.
 *   - Horizontal drag past a deadband → master-black ring rotation.
 *     The knurl marks visibly rotate.
 *   - Right-click → preview trigger (mirrors the press-down on the
 *     real lever, which outputs the preview signal).
 */
@customElement('sony-iris-lever')
export class SonyIrisLeverElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        inline-size: 100%;
        block-size: 100%;
        position: relative;
        aspect-ratio: 1;

        /* Matte rubber over-mould — same dark-range discipline as
         * the rest of the Sony control surface. The cap reads as
         * light-absorbing black; the highlight stop sits only ~18 %
         * brighter than the base. */
        --sil-cap: var(--control-cap, #1c1c1c);
        --sil-cap-highlight: color-mix(in oklab, var(--sil-cap) 82%, white);
        --sil-cap-shadow: color-mix(in oklab, var(--sil-cap) 50%, black);
        --sil-collar: color-mix(in oklab, var(--sil-cap) 75%, black);
        --sil-collar-highlight: color-mix(in oklab, var(--sil-collar) 75%, white);
        --sil-ring: color-mix(in oklab, var(--sil-cap) 65%, black);
        --sil-ring-highlight: color-mix(in oklab, var(--sil-ring) 70%, white);
        --sil-knurl: color-mix(in oklab, var(--sil-cap) 30%, black);
        --sil-hub: color-mix(in oklab, var(--sil-cap) 50%, black);
        --sil-tick: color-mix(in oklab, var(--sil-cap) 30%, white);
        --sil-well: color-mix(in oklab, var(--sil-cap) 40%, black);
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }

      .lever-assembly {
        transition: transform 90ms ease-out;
        cursor: grab;
        touch-action: none;
        -webkit-touch-callout: none;
      }

      .lever-assembly:active {
        cursor: grabbing;
      }

      :host([pressed]) .lever-assembly {
        transform: scale(0.97);
        transform-origin: 50px 50px;
      }
    `,
  ];

  /** Iris throw position, 0..1. */
  @property({ type: Number, attribute: 'throw-position' }) accessor throwPosition = 0.5;

  /** Master-black rotation, 0..1. */
  @property({ type: Number, attribute: 'master-black' }) accessor masterBlack = 0.5;

  /** Number of knurl ticks around the master-black ring. */
  @property({ type: Number, attribute: 'ring-detents' }) accessor ringDetents = 28;

  /** Visual press latch. */
  @property({ type: Boolean, reflect: true }) accessor pressed = false;

  /** Vertical pixels of drag for a full iris sweep. */
  @property({ type: Number, attribute: 'drag-sweep-px' }) accessor dragSweepPx = 220;

  /** Master-black horizontal-drag deadband in pixels. */
  @property({ type: Number, attribute: 'master-black-deadband-px' })
  accessor masterBlackDeadbandPx = 20;

  /** Master-black drag sweep in pixels beyond the deadband. */
  @property({ type: Number, attribute: 'master-black-sweep-px' })
  accessor masterBlackSweepPx = 240;

  #pointerId = -1;
  #dragStartClientX = 0;
  #dragStartClientY = 0;
  #dragStartThrow = 0;
  #dragStartMasterBlack = 0;
  #leftHeld = false;
  #rightHeld = false;

  #onPointerDown = (event: PointerEvent) => {
    if (this.#pointerId !== event.pointerId) {
      this.#pointerId = event.pointerId;
      this.#dragStartClientX = event.clientX;
      this.#dragStartClientY = event.clientY;
      this.#dragStartThrow = this.throwPosition;
      this.#dragStartMasterBlack = this.masterBlack;
      (event.currentTarget as Element).setPointerCapture(event.pointerId);
    }
    if (event.button === 0) this.#leftHeld = true;
    if (event.button === 2) {
      this.#rightHeld = true;
      this.pressed = true;
      this.dispatchEvent(new CustomEvent('stick.preview-trigger', {
        bubbles: true, composed: true,
      }));
    }
    event.preventDefault();
  };

  #onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.#pointerId) return;
    const dx = event.clientX - this.#dragStartClientX;
    const dy = event.clientY - this.#dragStartClientY;
    const dThrow = -dy / this.dragSweepPx;
    const nextThrow = Math.max(0, Math.min(1, this.#dragStartThrow + dThrow));
    if (nextThrow !== this.throwPosition) {
      this.throwPosition = nextThrow;
      this.dispatchEvent(new CustomEvent('stick.iris-change', {
        detail: { value: nextThrow }, bubbles: true, composed: true,
      }));
    }
    const absDx = Math.abs(dx);
    if (absDx > this.masterBlackDeadbandPx) {
      const dxBeyondDeadband = Math.sign(dx) * (absDx - this.masterBlackDeadbandPx);
      const dMb = dxBeyondDeadband / this.masterBlackSweepPx;
      const nextMb = Math.max(0, Math.min(1, this.#dragStartMasterBlack + dMb));
      if (nextMb !== this.masterBlack) {
        this.masterBlack = nextMb;
        this.dispatchEvent(new CustomEvent('stick.master-black-change', {
          detail: { value: nextMb }, bubbles: true, composed: true,
        }));
      }
    }
  };

  #onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.#pointerId) return;
    if (event.button === 0) this.#leftHeld = false;
    if (event.button === 2) {
      this.#rightHeld = false;
      this.pressed = false;
    }
    if (!this.#leftHeld && !this.#rightHeld) {
      const target = event.currentTarget as Element;
      if (target.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId);
      }
      this.#pointerId = -1;
    }
  };

  #onContextMenu = (event: MouseEvent) => {
    event.preventDefault();
  };

  /**
   * Render `count` knurl tick marks evenly spaced around a ring.
   * Each tick is a short radial dash on the visible annulus of the
   * master-black ring, catching simulated light from the upper-left.
   * The full ring rotates around the centre by the master-black
   * angle so the operator sees the knurl marks move with input.
   */
  #renderKnurl(radius: number, count: number, length: number, rotationDeg: number) {
    const ticks = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * 2 * Math.PI;
      const outerX = 50 + Math.cos(angle) * radius;
      const outerY = 50 + Math.sin(angle) * radius;
      const innerX = 50 + Math.cos(angle) * (radius - length);
      const innerY = 50 + Math.sin(angle) * (radius - length);
      const litness = Math.max(0, Math.cos(angle - Math.PI / 4));
      ticks.push(html`
        <line
          x1=${outerX}
          y1=${outerY}
          x2=${innerX}
          y2=${innerY}
          stroke="var(--sil-knurl)"
          stroke-width="0.35"
          opacity=${0.4 + 0.5 * litness}
        />
      `);
    }
    return html`<g transform="rotate(${rotationDeg} 50 50)">${ticks}</g>`;
  }

  override render() {
    // Master-black rotates the knurl ring visibly.
    const mbAngleDeg = (this.masterBlack - 0.5) * 270;

    // Throw position imparts a subtle offset on the centre hub
    // marker — the operator sees the hub indicator drift a touch
    // when the lever is at the extremes, hinting at the lever's
    // physical leaning state without breaking top-down doctrine.
    const throwOffset = (this.throwPosition - 0.5) * 6; // ±3 vbu drift

    return html`
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet"
           role="slider" aria-label="Sony iris control lever (top-down)">
        <title>Sony IRIS control lever</title>
        <desc>
          Top-down view of the iris control lever assembly. Concentric
          circles: chassis well, outer bezel collar, knurled
          master-black rotation ring, cap top, centre hub. Vertical
          drag drives iris; horizontal drag past the deadband drives
          master-black ring rotation; right-click outputs preview.
        </desc>

        <defs>
          <!-- Chassis well — deep dark recess where the lever sits.
               Slight inner shadow so the cap reads as raised above
               the chassis face. -->
          <radialGradient id="sil-well-grad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.7" stop-color="var(--sil-well)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sil-well) 60%, black)" />
          </radialGradient>

          <!-- Outer collar — the visible bezel ring just outside the
               cap. Reads as a step in the moulding catching ambient
               light from above. -->
          <radialGradient id="sil-collar-grad" cx="0.5" cy="0.42" r="0.55">
            <stop offset="0" stop-color="var(--sil-collar-highlight)" />
            <stop offset="0.6" stop-color="var(--sil-collar)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sil-collar) 60%, black)" />
          </radialGradient>

          <!-- Master-black knurled ring — the inner concentric ring
               between the collar and the cap top. Carries the
               cross-hatch knurl. -->
          <radialGradient id="sil-ring-grad" cx="0.5" cy="0.4" r="0.55">
            <stop offset="0" stop-color="var(--sil-ring-highlight)" />
            <stop offset="0.65" stop-color="var(--sil-ring)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sil-ring) 55%, black)" />
          </radialGradient>

          <!-- Cap top — matte rubber dome. Soft diffuse highlight
               from upper-left, no specular hotspot. -->
          <radialGradient id="sil-cap-grad" cx="0.4" cy="0.32" r="0.7">
            <stop offset="0" stop-color="var(--sil-cap-highlight)" />
            <stop offset="0.55" stop-color="var(--sil-cap)" />
            <stop offset="0.9" stop-color="var(--sil-cap-shadow)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sil-cap) 40%, black)" />
          </radialGradient>

          <!-- Centre hub — small dark spot at the very middle, the
               lever's mechanical pivot indicator. -->
          <radialGradient id="sil-hub-grad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stop-color="color-mix(in oklab, var(--sil-hub) 70%, black)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sil-hub) 90%, black)" />
          </radialGradient>
        </defs>

        <!-- Layer 1: chassis well, the deepest recess. -->
        <circle cx="50" cy="50" r="48" fill="url(#sil-well-grad)" />

        <!-- The whole lever assembly. Scales on press, no tilt
             (top-down doctrine — vertical tilt would put the
             operator beside the panel rather than seated in front). -->
        <g
          class="lever-assembly"
          @pointerdown=${this.#onPointerDown}
          @pointermove=${this.#onPointerMove}
          @pointerup=${this.#onPointerUp}
          @pointercancel=${this.#onPointerUp}
          @contextmenu=${this.#onContextMenu}
        >
          <!-- Layer 2: outer bezel collar. The visible annulus
               between r=36 (master-black ring outer) and r=43
               reads as the moulded step around the lever base. -->
          <circle cx="50" cy="50" r="43" fill="url(#sil-collar-grad)" />

          <!-- Layer 3: master-black rotation ring. Annulus between
               r=27 (cap edge) and r=36, knurled with rotating ticks. -->
          <circle cx="50" cy="50" r="36" fill="url(#sil-ring-grad)" />
          ${this.#renderKnurl(34, this.ringDetents, 1.6, mbAngleDeg)}

          <!-- Layer 4: cap top — the matte rubber disc the operator's
               thumb rests on. Soft dome gradient suggests a low
               convex surface. -->
          <circle cx="50" cy="50" r="27" fill="url(#sil-cap-grad)" />

          <!-- Cap rim shadow — a thin darker line at the outer edge
               of the cap, where the cap moulding catches less
               ambient light. -->
          <circle cx="50" cy="50" r="27"
            fill="none"
            stroke="color-mix(in oklab, var(--sil-cap) 50%, black)"
            stroke-width="0.4"
            opacity="0.55" />

          <!-- Layer 5: centre hub — small inset pivot indicator.
               Offsets slightly with iris throw so the operator
               sees movement at the lever's mechanical centre when
               the lever is leaned. The whole hub group translates;
               an internal tick rotates with master-black so the
               same element reflects both axes. -->
          <g transform="translate(0 ${throwOffset})">
            <circle cx="50" cy="50" r="6" fill="url(#sil-hub-grad)" />
            <line
              x1="50" y1="44"
              x2="50" y2="50"
              stroke="var(--sil-tick)"
              stroke-width="0.7"
              stroke-linecap="round"
              opacity="0.7"
              transform="rotate(${mbAngleDeg} 50 50)"
            />
          </g>
        </g>
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sony-iris-lever': SonyIrisLeverElement;
  }
}
